// Sale draft store (zustand). The "venta en curso" lives here between the moment
// the operator picks a product and the moment they confirm the sale.
//
// Each draft line represents one price/discount combination. Two units of the
// same product and price share a line through `qty`; a different price creates a
// separate line so the operator can always decide exactly which one to change.
//
// On confirm, the API receives this shape directly. See DATA-MODEL.md §5/§6.

import { create } from "zustand";

export type SaleDraftItem = {
  // Local id, only for React keys. Never sent to the API.
  localId: string;
  productId: number;
  productNameSnapshot: string;
  unitPriceCents: number;
  qty: number;
  // Discount per unit while the sale is being edited. When sending the sale to
  // the API it is multiplied by qty because SaleItem.lineDiscountCents stores
  // the total discount of the persisted line.
  lineDiscountCents: number;
  sizeGuideId: number | null;
  sizeGuideNameSnapshot: string | null;
  sizeSnapshot: string | null;
  // Per-line observation. It is part of the line identity: products with a
  // different observation must stay separated even when the price matches.
  note: string | null;
};

export type SaleDraft = {
  items: SaleDraftItem[];
  globalDiscountPct: number;
  // paymentMethodId is set when the operator picks one. The full paymentMethod
  // object (with snapshot fields) is held in UI state, not in this store.
  paymentMethodId: number | null;
  // customerId is set when the operator picks a customer from the 'Elegir
  // cliente' sheet. Only meaningful when paymentMethodId points at the
  // 'Cuenta corriente' method, but kept here as a draft-level concern so
  // confirming the sale passes it through without extra plumbing.
  customerId: number | null;
};

type SaleDraftStore = SaleDraft & {
  // actions
  addItem: (input: {
    productId: number;
    productName: string;
    unitPriceCents: number;
    lineDiscountCents?: number;
    sizeGuideId?: number | null;
    sizeGuideName?: string | null;
    size?: string | null;
    note?: string | null;
  }) => void;
  updateItem: (localId: string, patch: Partial<SaleDraftItem>) => void;
  incrementItem: (localId: string) => void;
  decrementItem: (localId: string) => void;
  removeItem: (localId: string) => void;
  setItemNote: (localId: string, note: string | null) => void;
  setGlobalDiscountPct: (pct: number) => void;
  hydrate: (input: { items: SaleDraftItem[]; globalDiscountPct?: number; customerId?: number | null }) => void;
  setPaymentMethod: (id: number | null) => void;
  setCustomer: (id: number | null) => void;
  clear: () => void;
};

const empty: SaleDraft = {
  items: [],
  globalDiscountPct: 0,
  paymentMethodId: null,
  customerId: null,
};

let localIdCounter = 0;
function nextLocalId(): string {
  localIdCounter += 1;
  return `li_${Date.now()}_${localIdCounter}`;
}

export const useSaleDraft = create<SaleDraftStore>((set) => ({
  ...empty,
  addItem: (input) =>
    set((s) => {
      const lineDiscountCents = input.lineDiscountCents ?? 0;
      const sizeGuideId = input.sizeGuideId ?? null;
      const sizeGuideNameSnapshot = input.sizeGuideName ?? null;
      const sizeSnapshot = input.size ?? null;
      const note = input.note?.trim() || null;
      const matchingIndex = s.items.findIndex(
        (item) =>
          item.productId === input.productId &&
          item.unitPriceCents === input.unitPriceCents &&
          item.lineDiscountCents === lineDiscountCents &&
          item.sizeGuideId === sizeGuideId &&
          item.sizeSnapshot === sizeSnapshot &&
          item.note === note
      );
      if (matchingIndex >= 0) {
        return {
          items: s.items.map((item, index) =>
            index === matchingIndex ? { ...item, qty: item.qty + 1 } : item
          ),
        };
      }
      return {
        items: [
          ...s.items,
          {
            localId: nextLocalId(),
            productId: input.productId,
            productNameSnapshot: input.productName,
            unitPriceCents: input.unitPriceCents,
            qty: 1,
            lineDiscountCents,
            sizeGuideId,
            sizeGuideNameSnapshot,
            sizeSnapshot,
            note,
          },
        ],
      };
    }),
  updateItem: (localId, patch) =>
    set((s) => ({
      items: s.items.map((it) => (it.localId === localId ? { ...it, ...patch } : it)),
    })),
  incrementItem: (localId) =>
    set((s) => ({
      items: s.items.map((item) =>
        item.localId === localId ? { ...item, qty: item.qty + 1 } : item
      ),
    })),
  decrementItem: (localId) =>
    set((s) => ({
      items: s.items.flatMap((item) =>
        item.localId !== localId
          ? [item]
          : item.qty > 1
            ? [{ ...item, qty: item.qty - 1 }]
            : []
      ),
    })),
  removeItem: (localId) =>
    set((s) => ({ items: s.items.filter((it) => it.localId !== localId) })),
  setItemNote: (localId, note) =>
    set((s) => ({
      items: s.items.map((it) =>
        it.localId === localId ? { ...it, note: note?.trim() ? note.trim() : null } : it
      ),
    })),
  setGlobalDiscountPct: (pct) =>
    set({ globalDiscountPct: Math.min(99, Math.max(0, Math.trunc(pct))) }),
  hydrate: ({ items, globalDiscountPct = 0, customerId = null }) =>
    set({ items, globalDiscountPct, customerId, paymentMethodId: null }),
  setPaymentMethod: (id) => set({ paymentMethodId: id }),
  setCustomer: (id) => set({ customerId: id }),
  clear: () => set({ ...empty }),
}));

// Selectors. Keep them here so the shape is documented in one place.
export const selectItemCount = (s: SaleDraftStore) =>
  s.items.reduce((total, item) => total + item.qty, 0);
export const selectItemSubtotalCents = (s: SaleDraftStore) =>
  s.items.reduce(
    (acc, it) => acc + it.qty * (it.unitPriceCents - it.lineDiscountCents),
    0
  );
export const selectGlobalDiscountCents = (s: SaleDraftStore) =>
  Math.round((selectItemSubtotalCents(s) * s.globalDiscountPct) / 100);
